import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import SectionBadge from "@/components/site/SectionBadge";
import ContextualCTA from "@/components/site/v2/ContextualCTA";
import BookConsultationDialog from "@/components/site/BookConsultationDialog";
import JoinUsDialog from "@/components/site/JoinUsDialog";
import { usePageMeta } from "@/lib/usePageMeta";
import { track, trackTool } from "@/lib/analytics";
import { TOOL_SOLUTION, getSolution } from "@/data/solutions";
import { publishedBySlug, PUBLISHED_TOOLS, categoryOf } from "@/data/tools/registry";
import { TOOL_COMPONENTS } from "@/components/tools";
import ToolReportButton from "@/components/tools/ToolReportButton";
import { GRID } from "@/components/tools/ui";
import NotFoundPage from "@/pages/NotFoundPage";

// Per-tool editorial content: intro, methodology, worked example, FAQs.
// Keys = tool id. Only published tools need entries.
import { TOOL_CONTENT } from "@/data/tools/toolContent";

export default function ToolPage() {
  const { slug } = useParams();
  const tool = publishedBySlug(slug);
  const [bookOpen, setBookOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);

  usePageMeta({
    title: tool ? `${tool.name} — Free Tool | BLEZZINGS` : "Tool Not Found | BLEZZINGS",
    description: tool ? TOOL_CONTENT[tool.id]?.meta || tool.output : undefined,
    canonical: tool ? `https://blezzings.co${tool.url}` : false,
    robots: tool ? undefined : "noindex, follow",
    breadcrumbs: tool
      ? [
          { name: "Home", url: "https://blezzings.co/" },
          { name: "Growth Tools", url: "https://blezzings.co/tools" },
          { name: tool.name, url: `https://blezzings.co${tool.url}` },
        ]
      : undefined,
    webapp: tool ? { name: tool.name, description: tool.output, url: `https://blezzings.co${tool.url}` } : undefined,
  });

  // GA4 funnel events (taxonomy 2026-10-23): tool_view → tool_start → tool_complete.
  // tool_start = first real interaction with any control in the tool body.
  // tool_complete = the results/report row scrolls into view after a start
  // (a usable result has been reached). Identifiers only — never input values.
  const bodyRef = useRef(null);
  const funnel = useRef({ started: false, completed: false });
  useEffect(() => {
    funnel.current = { started: false, completed: false };
    if (!tool) return;
    trackTool("tool_view", tool);
    const node = bodyRef.current;
    if (!node) return;
    const onInteract = (e) => {
      if (funnel.current.started) return;
      if (e.target.closest("input, select, textarea, button, [role='button'], [data-slot]")) {
        funnel.current.started = true;
        trackTool("tool_start", tool);
        // watch for the report row (end of results) entering the viewport
        const reportRow = node.querySelector('[data-testid="tool-report-btn"]')?.closest("div");
        if (reportRow && "IntersectionObserver" in window) {
          const io = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && !funnel.current.completed) {
              funnel.current.completed = true;
              trackTool("tool_complete", tool);
              io.disconnect();
            }
          }, { threshold: 0.5 });
          io.observe(reportRow);
        }
      }
    };
    node.addEventListener("input", onInteract, true);
    node.addEventListener("click", onInteract, true);
    return () => {
      node.removeEventListener("input", onInteract, true);
      node.removeEventListener("click", onInteract, true);
    };
  }, [tool]);

  if (!tool) return <NotFoundPage />;

  const cat = categoryOf(tool.category);
  const content = TOOL_CONTENT[tool.id] || {};
  const Body = TOOL_COMPONENTS[tool.id];
  const related = PUBLISHED_TOOLS.filter((t) => t.id !== tool.id && (t.primary === tool.primary || t.category === tool.category)).slice(0, 3);
  const solution = TOOL_SOLUTION[tool.slug] ? getSolution(TOOL_SOLUTION[tool.slug]) : null;

  return (
    <div data-testid={`tool-page-${tool.id}`} className="relative">
      <Nav onBook={() => setBookOpen(true)} />

      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-10 md:pb-14 bg-white">
        <div className={`${GRID} max-w-5xl`}>
          <SectionBadge label={`Free Tool · ${cat.name}`} />
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05]">{tool.name}</h1>
          <p className="mt-5 font-serif-display text-xl md:text-2xl text-zinc-500 leading-snug max-w-3xl">{tool.hook}</p>
          <p className="mt-5 text-base text-zinc-600 leading-relaxed max-w-2xl">{content.intro || tool.output}</p>
          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-zinc-500">
            <span>For: {tool.buyer}</span>
            <span>·</span>
            <span>Free — no sign-up for results</span>
            <span>·</span>
            <Link to={tool.primaryUrl} className="underline underline-offset-4 hover:text-black" data-testid="tool-owner-link">
              Related service: {content.ownerName || "View the practice"}
            </Link>
          </div>
        </div>
      </section>

      {/* Interactive tool */}
      <section className="pb-16 md:pb-20 bg-white" data-testid="tool-body" ref={bodyRef}>
        <div className={`${GRID} max-w-5xl`}>
          <div className="border-t-2 border-black pt-8">
            {Body ? <Body /> : null}
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <ToolReportButton tool={tool} />
            </div>
            {solution && (
              <p className="mt-6 text-sm text-zinc-600" data-testid="tool-solution-link">
                Need a deeper, data-led diagnosis?{" "}
                <Link
                  to={`/solutions/${solution.id}`}
                  data-testid="tool-solution-link-anchor"
                  className="text-black font-medium underline underline-offset-4 decoration-zinc-300 hover:decoration-black transition-colors"
                >
                  See {solution.name} — {solution.descriptor}
                </Link>
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Methodology + example + FAQs */}
      <section className="py-14 md:py-16 bg-[#fafafa]">
        <div className={`${GRID} max-w-5xl`}>
          <div className="grid md:grid-cols-2 gap-10">
            {content.method && (
              <div data-testid="tool-method">
                <h2 className="font-serif-display text-2xl text-black">How it works</h2>
                <div className="mt-4 space-y-3 text-sm text-zinc-600 leading-relaxed">
                  {content.method.map((m, i) => <p key={i}>{m}</p>)}
                </div>
              </div>
            )}
            {content.example && (
              <div data-testid="tool-example">
                <h2 className="font-serif-display text-2xl text-black">Worked example</h2>
                <div className="mt-4 border border-zinc-200 bg-white p-5 text-sm text-zinc-700 leading-relaxed">
                  {content.example}
                  <p className="mt-3 text-xs text-zinc-400">Illustrative figures, not a client outcome.</p>
                </div>
              </div>
            )}
          </div>
          {content.faqs?.length > 0 && (
            <div className="mt-12" data-testid="tool-faqs">
              <h2 className="font-serif-display text-2xl text-black">Questions, answered</h2>
              <div className="mt-5 space-y-4">
                {content.faqs.map((f, i) => (
                  <div key={i} className="border-b border-black/10 pb-4">
                    <div className="text-[15px] font-medium text-black">{f.q}</div>
                    <p className="mt-1.5 text-sm text-zinc-600 leading-relaxed">{f.a}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          <p className="mt-8 text-xs text-zinc-400">Version 1.0 · Reviewed 2026-10 · Methodology is fixed and version-controlled; results depend on your inputs.</p>
        </div>
      </section>

      {/* Related tools */}
      {related.length > 0 && (
        <section className="py-14 md:py-16 bg-white">
          <div className={`${GRID} max-w-5xl`}>
            <div className="flex items-end justify-between gap-6">
              <h2 className="font-serif-display text-2xl md:text-3xl">Keep going</h2>
              <Link to="/tools" className="text-sm font-medium underline underline-offset-4" data-testid="tool-all-link">All tools</Link>
            </div>
            <div className="mt-6 grid sm:grid-cols-3 gap-4">
              {related.map((t) => (
                <Link
                  key={t.id}
                  to={t.url}
                  data-testid={`tool-related-${t.id}`}
                  onClick={() => track("tool_related_click", { from: tool.id, to: t.id })}
                  className="group border border-zinc-200 bg-white p-5 hover:border-black transition-colors"
                >
                  <div className="overline">{t.id}</div>
                  <div className="mt-2 font-serif-display text-lg leading-snug group-hover:text-zinc-700">{t.name}</div>
                  <div className="mt-2 text-xs text-zinc-500 leading-relaxed">{t.output}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <ContextualCTA
        testid="tool-cta"
        heading={content.ctaHeading || "Want this done with your real numbers?"}
        body={content.ctaBody || "BLEZZINGS runs this analysis inside a focused engagement — with your data, your market and a plan to act on it."}
        label={content.ctaLabel || "Discuss Your Numbers"}
        onBook={() => setBookOpen(true)}
      />

      <BookConsultationDialog open={bookOpen} onOpenChange={setBookOpen} service={tool.name} />
      <JoinUsDialog open={joinOpen} onOpenChange={setJoinOpen} />
      <Footer onJoin={() => setJoinOpen(true)} />
    </div>
  );
}
